import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import RecorderListPresenter from "./RecorderListPresenter";
import * as s from "../../RecordersStyled";
import TableButton from "components/atoms/buttons/TableButton";
import Type from "components/atoms/text/labels/Type";
import ONLINE_ICON from "assets/icons/online.svg";
import OFFLINE_ICON from "assets/icons/offline.svg";
import TableTitle from "components/blocks/table/TableTitle";
import { useOutletContext, useSearchParams } from "react-router-dom";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useQuery } from "react-query";
import { getRecorderList } from "api/recorderAPI";
import { checkStatus, convertType, formatNumber } from "utils/functions";
import Status, {
  StatusName,
  StatusValue,
} from "components/blocks/status/Status";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import {
  LicenseCount,
  MergedRecorder,
  MergedRecorderListResponse,
  Recorder,
  SubscriptionInfo,
} from "api/interfaces/recorderInterface.interface";
import { AlertRecorerTypeFilter } from "utils/AlertUtil";
import WebAppLink from "components/blocks/link/WebAppLink";
import { NeedActionCell } from "../NeedActionCell";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import { useMenu } from "hook/useMenu";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { ExpandableConfig } from "antd/es/table/interface";
import RecorderListExpand from "./RecorderListExpand";
import * as mediaQuery from "components/MediaQuery";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { FaSync } from "react-icons/fa";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { useFlag } from "@unleash/proxy-client-react";
import FirmwareVersionColumn from "pages/maintenance/firmware/FirmwareUpgrade/FirmwareVersionColumn";
import {
  COVA_MODEL,
  FIRMWARE_FEATURE_KEY,
} from "api/interfaces/managerInterface.interface";
import { postFirmwareVersionInfo } from "api/manageAPI";

interface LicenseRowType {
  count: number;
  status: string;
  trialDays?: number;
}
export interface DataRow
  extends Omit<Recorder, "status" | "mergedSystemName" | "createdAt" | "id"> {
  title: string;
  status: string;
  version: string;
  cameras: number;
  licenses: LicenseRowType;
  enableEdit?: boolean;
  isMerged?: boolean;
  hasAuthError?: boolean;
  hasIncompleteRegistration?: boolean;
  hasDetached?: boolean;
  hostValidation: boolean;
  row: MergedRecorder | Recorder;
  mergedSystemName: string;
  createdAt?: string;
  recordId?: string;
}

export function mergedRecorderToDataRow(recorder: MergedRecorder): DataRow {
  return {
    ...recorder,
    id: recorder.mergedSystemId,
    recordId:
      recorder.recorderCount === 1 && recorder.recorders[0]?.recorderId
        ? recorder.recorders[0].recorderId
        : recorder.mergedSystemId,
    title: recorder.mergedSystemName,
    status:
      recorder.recorderCount !== undefined &&
      recorder.onlineStatusCount !== undefined
        ? checkStatus(recorder.recorderCount, recorder.onlineStatusCount)
        : "offline",
    brand: recorder.recorders[0]?.brand,
    version: recorder.firmwareVersion,
    cameras: recorder.cameraCount,
    licenses: {
      count: 0,
      status: "PREPARING",
    },
    hasAuthError: recorder.recorders.some((child) => child.isAuthError),
    hasDetached: recorder.recorders.some((child) => child.isDetached),
    hasIncompleteRegistration: recorder.incompleteRegistration,
    row: recorder,
    isUpdate: recorder.isUpdate,
    mergedSystemName: recorder.mergedSystemName,
    createdAt: recorder.createdAt ? String(recorder.createdAt) : undefined,
  } as unknown as DataRow;
}
const statusInitValue = {
  totalTitle: "Recorders",
  totalValue: 0,
  items: [
    <>
      <s.Label>
        <img src={ONLINE_ICON} alt="online icon" />
        <StatusName>Online</StatusName>
      </s.Label>
      <StatusValue>0</StatusValue>
    </>,
    <>
      <s.Label>
        <img src={OFFLINE_ICON} alt="offline icon" />
        <StatusName>Offline</StatusName>
      </s.Label>
      <StatusValue>0</StatusValue>
    </>,
  ],

  cloudStatus: false,
  autoUpdate: false,
};
const initialQueryinfo = {
  ...baseQueryInfo,
  pageLimit: 20,
  sortType: "mergedSystemName",
  type: null,
};
interface Props {}

interface Context {
  keyword: string;
  setLicenseCount: (licenseCount: LicenseCount) => void;
  setSubscriptionInfo: (subscriptionInfo: SubscriptionInfo) => void;
  setTitleComponent: (titleComponent: string) => void;
  setCenterChildren: (centerChildren: React.ReactNode) => void;
}
export default function RecorderListContainer(props: Props): JSX.Element {
  const outletContext = useOutletContext<Context>();

  const {
    setLicenseCount,
    setSubscriptionInfo,
    setTitleComponent,
    setCenterChildren,
  } = outletContext ?? {};
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const firmwareUpgradeVersion16CH = useQuery(
    ["firmwareUpgradeVersion16CH"],
    () =>
      postFirmwareVersionInfo({
        featureKey: FIRMWARE_FEATURE_KEY.FIRMWARE_UPGRADE,
        displayType: "COVA",
        model: COVA_MODEL.DW_COVA16,
      }),
    {
      enabled: !!selectedAccount.accountId,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );
  const firmwareUpgradeVersion4CH = useQuery(
    ["firmwareUpgradeVersion4CH"],
    () =>
      postFirmwareVersionInfo({
        featureKey: FIRMWARE_FEATURE_KEY.FIRMWARE_UPGRADE,
        displayType: "COVA",
        model: COVA_MODEL.DW_COVA4,
      }),
    {
      enabled: !!selectedAccount.accountId,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );
  const { menuNavigation } = useMenu({});

  const [recorder, setRecorder] = useState<DataRow[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({});
  const [isEditModal, setIsEditModal] = useState<boolean>(false);

  const recorderQueryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...initialQueryinfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
    };
  }, [searchParams, pageInfo]);

  // queryInfo를 바꾸고 싶을 때는 이 함수를 통해 searchParams만 업데이트
  const updateQueryInfo = useCallback(
    (partial: Partial<BasePagination>) => {
      let shouldReplace = false;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(partial).forEach(([key, value]) => {
            if (
              key === "pageNum" &&
              prev.get(key) === null &&
              Number(value) === 0
            ) {
              next.delete(key);
              return;
            }

            if (value === null || value === undefined || value === "") {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          });
          if (next.toString() === prev.toString()) {
            return prev;
          }
          shouldReplace = true;
          return next;
        },
        { replace: shouldReplace }
      );
    },
    [setSearchParams]
  );
  const recorderQueryKey = useQueryKey(recorderQueryInfo);
  const [status, setStatus] = useState(statusInitValue);

  const [isDeregisterHelp, setIsDeregisterHelp] = useState<boolean>(false);

  const [isLicenseModal, setIsLicenseModal] = useState<boolean>(false);
  const [isRemoveLicenseModal, setIsRemoveLicenseModal] =
    useState<boolean>(false);
  const [selectRow, setSelectRow] = useState<DataRow>();

  const [expandedRowKeys, setExpandedRowKeys] = useState<string | null>(null);
  const isMobile = mediaQuery.useIsMobile();
  const isBigScreen = mediaQuery.useIsBigScreen();
  const [isUpdateFirmwareModal, setIsUpdateFirmwareModal] =
    useState<boolean>(false);
  const isFirmwareUpgradeEnable = useFlag("feature_manage_firmwareUpgrade");

  const initialData = () => {
    setRecorder([]);
    setLicenseCount({
      totalCameraCount: 0,
      totalLicensedCameraCount: 0,
    });
    setStatus(statusInitValue);
  };

  const onClickAutoUpdate = selectedAccount.userPermission.manageMaintenance
    ? () => {
        menuNavigation({
          to: `/maintenance/firmware`,
        });
      }
    : undefined;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { refetch } = useQuery(
    ["recorder", recorderQueryKey, selectedAccount.accountId],
    () =>
      getRecorderList({
        payload: recorderQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      enabled: !!selectedAccount.accountId,
      refetchInterval: 30000,
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: MergedRecorderListResponse) => {
        if (res.error !== 0 || !res.result) {
          initialData();
          return;
        }

        setRecorder(
          (res.result.mergedSystemList || []).map(
            (recorder: MergedRecorder): DataRow & { key: string } => {
              return {
                ...mergedRecorderToDataRow(recorder),
                key: recorder.mergedSystemId,
              };
            }
          )
        );

        setStatus({
          totalTitle: "Recorders",
          totalValue: res.result.summary.totalRecorderCount,
          items: [
            <>
              <s.Label>
                <img src={ONLINE_ICON} alt="online icon" width={16} />
                <StatusName>Online</StatusName>
              </s.Label>
              <StatusValue>
                {formatNumber(res.result.summary.onlineStatusCount)}
              </StatusValue>
            </>,
            <>
              <s.Label>
                <img src={OFFLINE_ICON} alt="offline icon" width={16} />
                <StatusName>Offline</StatusName>
              </s.Label>
              <StatusValue>
                {formatNumber(
                  res.result.summary.totalRecorderCount -
                    res.result.summary.onlineStatusCount
                )}
              </StatusValue>
              {res.result.summary.autoUpdate && isFirmwareUpgradeEnable && (
                <s.Label
                  onClick={onClickAutoUpdate}
                  data-testid="recorders-recorders-list-label-click-auto-update"
                >
                  <FaSync />
                  <span>Auto Update</span>
                </s.Label>
              )}
            </>,
          ],

          cloudStatus: res.result.summary.cloudStatus,
          autoUpdate: res.result.summary.autoUpdate,
        });
        setLicenseCount({
          totalCameraCount: res.result.licenseCount.totalCameraCount,
          totalLicensedCameraCount:
            res.result.licenseCount.totalLicensedCameraCount,
        });
        setSubscriptionInfo({ ...res.result.subscription });
        setPageInfo(res.page);
      },
      onError: () => {
        initialData();
      },
      onSettled: () => {
        setPending(false);
      },
    }
  );

  const onChangeReportType = useCallback(
    (value: string | undefined) => {
      if (value === undefined) return;
      updateQueryInfo({
        type: value === "all" ? null : value,
        pageNum: 0,
      });
    },
    [updateQueryInfo]
  );
  const onHeaderCell = (column: ColumnType<DataRow>) => ({
    onClick: () => {
      if (column.key) {
        updateQueryInfo({
          sortType: column.key as string,
          sortDirection: onSortAntd(recorderQueryInfo.sortDirection),
        });
      }
    },
  });
  const onClickUpdateFirmware = (row: DataRow) => {
    setSelectRow(row);
    setIsUpdateFirmwareModal(true);
  };

  const columns: TableColumnsType<DataRow> = [
    {
      key: "type",
      title: (
        <TableColumnFilterTitle
          title="Type"
          filters={AlertRecorerTypeFilter}
          onClick={onChangeReportType}
          data-testid="recorders-recorders-list-table-column-filter-title-change-report-type"
        />
      ),

      width: isMobile ? 90 : 110,
      render: (value, row) => (
        <Type
          type={convertType(row)}
          licenseType={row.licenseType}
          isCloudCam={row.isCloudCam}
        />
      ),
    },
    {
      title: "Title",
      sorter: true,
      key: "mergedSystemName",
      defaultSortOrder: "ascend",
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "mergedSystemName"),
      onHeaderCell: onHeaderCell,
      render: (value, row) =>
        row.recorderCount !== undefined && row.recorderCount > 0 ? (
          <TableTitle row={row} cloudStatus={status.cloudStatus} />
        ) : (
          <Fragment />
        ),

      ellipsis: true,
    },
    {
      key: "access",
      title: "Access",
      render: (value, row) => (
        <s.WebAppRow onClick={(e) => e.stopPropagation()}>
          <WebAppLink
            {...row}
            displayType={convertType(row)}
            accountId={selectedAccount.accountId}
            recorderId={row.recordId || ""}
            cloudStatus={status.cloudStatus}
          />
        </s.WebAppRow>
      ),

      hidden:
        selectedAccount.accountLevel !== LEVEL_TYPE.EU ||
        selectedAccount.isHidden ||
        !selectedAccount.isEnabled,
      width: isMobile ? 120 : "10%",
    },
    {
      title: "Version",
      render: (value, row) => (
        <FirmwareVersionColumn
          firmwareUpgradeVersion16CH={
            firmwareUpgradeVersion16CH.data?.result?.minVersion
          }
          firmwareUpgradeVersion4CH={
            firmwareUpgradeVersion4CH.data?.result?.minVersion
          }
          autoUpdate={status.autoUpdate}
          row={row as unknown as MergedRecorder}
          onClickUpdateFirmware={(row) =>
            onClickUpdateFirmware(row as unknown as DataRow)
          }
        />
      ),

      responsive: ["xl"],
      width: isBigScreen ? "15%" : "10%",
    },
    {
      title: "Registered Date",
      render: (value, row) =>
        getAccountTimeFormatDate(
          row.createdAt || "",
          selectedAccount,
          true,
          false
        ),
      responsive: ["lg"],
      width: isBigScreen ? "15%" : 150,
      sorter: true,
      key: "createdAt",
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "createdAt"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Cameras",
      render: (value, row) =>
        row.cameras > 0 ? (
          <s.RowLink
            onClick={() => {
              if (!selectedAccount.isEnabled) return;
              menuNavigation({
                to: `/recorders/list/cameras/${row.mergedSystemId}${
                  row.recorderId ? `/${row.recorderId}` : ""
                }`,
              });
            }}
            disabled={!selectedAccount.isEnabled}
            data-testid="recorders-recorders-list-row-link-menu-navigation"
          >
            {row.cameras}
          </s.RowLink>
        ) : (
          row.cameras
        ),

      align: "center",
      width: isMobile ? 60 : 100,
    },
    {
      title: "",
      width: 150,
      render: (value, row) =>
        selectedAccount.isEnabled && (
          <NeedActionCell recorder={row as DataRow & MergedRecorder} />
        ),

      responsive: ["xl"],
    },
    {
      title: "",
      width: isMobile ? 40 : 80,
      render: (row, index) => (
        <RBACWrapper requiredPermissions="manageRecordersAndGroups">
          <s.Row className="table-button-wrapper">
            <TableButton
              label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                onClickEdit(row);
              }}
              data-testid={`recorders-recorders-list-table-button-edit-${index}`}
            />
          </s.Row>
        </RBACWrapper>
      ),

      key: "edit",
      align: "right",
    },
  ];

  const onModal = (type: string, row?: DataRow) => {
    if (type === "license") {
      setIsLicenseModal(!isLicenseModal);
    }
    if (type === "remove") {
      setIsRemoveLicenseModal(!isRemoveLicenseModal);
    }
    if (type === "edit") {
      setIsEditModal(!isEditModal);
    }
  };

  const onClickEdit = (row: DataRow) => {
    setIsEditModal(true);
    setSelectRow(row);
    // debouncedNavigate({
    //   to: "/recorders/list/edit",
    //   queryString: {
    //     isMergedSystem: row.recorderCount > 1,
    //     systemId: row.recordId,
    //   },
    // });
  };

  const [pending, setPending] = useState<boolean>(true);

  const expandableProps = useMemo<ExpandableConfig<DataRow>>(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys: expandedRowKeys ? [expandedRowKeys] : [],
      onExpand: (expanded: boolean, record: DataRow) => {
        if (expanded) {
          setExpandedRowKeys(record.mergedSystemId);
        } else {
          setExpandedRowKeys(null);
        }
      },
      expandedRowRender: (record: DataRow) => (
        <RecorderListExpand
          mergedSystemId={record.mergedSystemId}
          columns={columns as TableColumnsType<Recorder>}
          queryInfo={recorderQueryInfo}
        />
      ),

      rowExpandable: (record: DataRow) =>
        convertType(record) === "SPECTRUM" && record.recorderCount > 1,
    };
  }, [expandedRowKeys, onClickEdit]);
  const onChangePage = (page: number, totalRows: number) => {
    updateQueryInfo({ pageNum: page - 1 });
  };

  const onOfflineStatus = useMemo(() => {
    return {
      ...status,
      items: selectedAccount.isEnabled ? status.items : [],
    };
  }, [selectedAccount.isEnabled, status]);

  useStompDebouncedRefetch({
    selector: (state) =>
      ({
        recorderListUpdated: state.stomp.recorderListUpdated,
        subscriptionActivatedUpdated:
          state.subscriptionStomp.subscriptionActivatedUpdated,
      } as Record<string, unknown>),
    stompKey: [
      "recorderListUpdated",
      "subscriptionActivatedUpdated",
    ] as StompDateSignalKey[],
    refetch: refetch,
  });

  useEffect(() => {
    const centerChildren = <Status status={onOfflineStatus} />;
    setTitleComponent("Recorder List");
    setCenterChildren(centerChildren);
  }, [onOfflineStatus, setCenterChildren, setTitleComponent]);

  console.log(recorder);

  return (
    <RecorderListPresenter
      onModal={onModal}
      selectRow={selectRow as DataRow}
      data={recorder}
      columns={columns as TableColumnsType<DataRow>}
      paginationInfo={recorderQueryInfo}
      onChangePage={onChangePage}
      initialPending={pending}
      isDeregisterHelp={isDeregisterHelp}
      closeDeregisterHelp={() => {
        setIsDeregisterHelp(false);
      }}
      expandableProps={expandableProps}
      isUpdateFirmwareModal={isUpdateFirmwareModal}
      onCancelUpdateFirmware={() => setIsUpdateFirmwareModal(false)}
      isEditModal={isEditModal}
      openDeregisterHelp={() => setIsDeregisterHelp(true)}
      autoUpdate={status.autoUpdate}
    />
  );
}
